import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { IRouteRequestState } from "types/routeRequestState";
import { getSessionStorage } from "helpers/session-storage-helper/session-storage-helper";
import { useCartAPI } from "store/cartStore";
import { usePlaceAPI } from "store/placeStore";
import { useMenuAPI } from "store/menuStore";
import { useDeliveryOptionsAPI } from "store/deliveryOptionsStore";

/**
 * Handles all requests that need to be made initially for this route.
 * Initally and until all requests have finished, will return @param isLoading = true.
 * If any request fails, will return @param error with error contents.
 *
 * Used to display a shared initial loading and error state for a route.
 *
 * @returns shared loading state and error state for all requests.
 */
export const useMenuRouteRequests = (): IRouteRequestState => {
	const { placeId, menuId, appAlias } = useParams<"placeId" | "menuId" | "appAlias">();
	const { getMenu } = useMenuAPI();
	const { getRootDeliveryOption } = useDeliveryOptionsAPI();
	const { getCart } = useCartAPI();
	const { getPlace } = usePlaceAPI();

	const [requestState, setRequestState] = useState<IRouteRequestState>({
		isLoading: true,
		error: undefined,
	});

	useEffect(() => {
		const fetch = async () => {
			try {
				if (!placeId) throw new Error();
				if (!menuId) throw new Error();
				if (!appAlias) throw new Error();

				// Required ids exist, start fetching route data
				await getMenu(menuId);

				const { eatHereRootResourceId } = await getPlace(placeId);
				if (eatHereRootResourceId) {
					await getRootDeliveryOption(eatHereRootResourceId);
				}

				const cartId = getSessionStorage<string>("cartId");

				if (cartId) {
					await getCart(cartId);
				}
				setRequestState({
					isLoading: false,
					error: undefined,
				});
			} catch (error) {
				setRequestState({
					isLoading: false,
					error,
				});
			}
		};
		fetch();
	}, [placeId, menuId, appAlias, getMenu, getPlace, getRootDeliveryOption, getCart]);

	return requestState;
};
